import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { portalGet } from '@/lib/portal-api';

interface PortalUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  companyId: string;
  companyName: string;
}

interface PortalAuthContextType {
  user: PortalUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextType | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('crm_portal_access_token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      const data = await portalGet<PortalUser>('/me');
      setUser(data);
    } catch {
      localStorage.removeItem('crm_portal_access_token');
      localStorage.removeItem('crm_portal_refresh_token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email: string, password: string) => {
    const res = await fetch('/api/portal/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error?.message || 'Login failed');
    localStorage.setItem('crm_portal_access_token', data.data.accessToken);
    localStorage.setItem('crm_portal_refresh_token', data.data.refreshToken);
    setUser(data.data.user);
  };

  const logout = () => {
    localStorage.removeItem('crm_portal_access_token');
    localStorage.removeItem('crm_portal_refresh_token');
    setUser(null);
  };

  return (
    <PortalAuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, login, logout }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortalAuth must be used within PortalAuthProvider');
  return ctx;
}
